import { useMemo } from "react";
import React, { useEffect, useRef, useState } from 'react'
import Store from '../store/Store'
import { DownloadButton } from "./ui/DownloadButton";
import { Button } from "./ui/Button";
import {
	IconInfoError,
	IconSearch,
	AnalyseIcon,
} from './Icons'
import 'rsuite/DateRangePicker/styles/index.css'
import './StyledComponents.scss'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { OrderItem } from './features/OrderItem'
export { DatePicker } from './ui/DatePicker'
export type { DatePickerProps, DatePickerValue } from './ui/DatePicker'
export { Button } from './ui/Button'
export type { ButtonProps } from './ui/Button'
export { DownloadButton } from './ui/DownloadButton'
export type { DownloadButtonProps } from './ui/DownloadButton'
export { UploadButton } from './ui/UploadButton'
export type { UploadButtonProps } from './ui/UploadButton'
export { OrderItem } from './features/OrderItem'
export type { OrderItemData, OrderItemProps } from './features/OrderItem'

//============================================================================= RedesignDropdown
interface RedesignDropdownProps {
	content?: Array<{
		_selected?: boolean;
		name?: string;
		description?: string;
	}>;
	onChange?: (
		content: Array<{
			_selected?: boolean;
			name?: string;
			description?: string;
		}>
	) => void;
	placeholder?: string;
	multiple?: boolean;
	['data-pw']?: string;
}

export const RedesignDropdown: React.FC<RedesignDropdownProps> = props => {
	const [content, setContent] = useState(props.content ?? []);
	const [open, setOpen] = useState(false);
	const dropdownRef = React.useRef<HTMLDivElement>(null);

	useEffect(() => {
		setContent(props.content ?? []);
	}, [props.content]);

	useEffect(() => {
		if (!open) return;
		function handleClickOutside(event: MouseEvent) {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [open]);

	const selectedItems = content.filter(e => e._selected);

	const selectedLabel =
		selectedItems.map(e => e.name).join(', ') ||
		props.placeholder ||
		'Verwendungszweck wählen';

	const handleSelect = (index: number) => {
		let updated;

		if (props.multiple) {
			updated = content.map((e, i) =>
				i === index ? { ...e, _selected: !e._selected } : e
			);
		} else {
			updated = content.map((e, i) => ({
				...e,
				_selected: i === index,
			}));
			setOpen(false);
		}

		setContent(updated);
		console.log('updated content:', updated);
		props.onChange?.(updated);
	};

	return (
		<div className="w-full" ref={dropdownRef}>
			<button
				type="button"
				data-pw={props['data-pw']}
				onClick={() => setOpen(!open)}
				className="flex h-[48px] w-full items-center justify-between rounded-full border border-[#4A4A4A] bg-white px-6"
			>
				<span
					className={`text-[17px] ${selectedItems.length === 0 ? 'text-[#9A9A9A]' : 'text-[#2B2B2B]'
						}`}
				>
					{selectedLabel}
				</span>

				<svg
					className={`transition ${open ? 'rotate-180' : ''}`}
					width="22"
					height="22"
					viewBox="0 0 24 24"
					fill="none"
				>
					<path
						d="M6 9L12 15L18 9"
						stroke="#2B2B2B"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>

			{open && (
				<div className="w-full mt-2 rounded-xl border border-[#E2E6EA] bg-white shadow-md overflow-hidden">
					{content.map((item, i) => (
						<button
							type="button"
							key={i}
							onClick={() => handleSelect(i)}
							className={`flex w-full items-center gap-3 px-5 py-4 text-left ${item._selected ? 'bg-[#EEF4FA]' : 'hover:bg-[#F7F9FB]'
								}`}
							data-pw={`dropdown-option-${item.name || i}`}
							data-pw-selected={item._selected ? 'true' : 'false'}
						>
							<div className="w-5 h-5 flex items-center justify-center shrink-0">
								{item._selected && (
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
									>
										<path
											d="M3.5 8L6.5 11L12.5 5"
											stroke="#2B2B2B"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								)}
							</div>

							<div className="text-[15px] text-[#333]">
								<span>{item.name}</span>
								{item.description && (
									<span className="italic text-[#666] ml-1">
										{item.description}
									</span>
								)}
							</div>
						</button>
					))}
				</div>
			)}
		</div>
	);
};
//=============================================================================
// MissionPurpose
interface MissionPurposeProps {
	value: string;
	content?: Array<{
		name: string;
		title?: string;
		description?: string;
		icon?: string;
		purposes?: Array<{
			name: string;
			title?: string;
			description?: string;
			image?: string;
			_selected?: boolean;
			resultTypes?: Array<{
				id?: number;
				_selected?: boolean;
				name?: string;
				description?: string;
			}>;
			_selectedType?: number;
		}>;
	}>;
	onChange?: () => void;
}

interface ResultType {
	id?: number;
	_selected?: boolean;
	name?: string;
	description?: string;
}

interface Purpose {
	name: string;
	title?: string;
	description?: string;
	image?: string;
	_selected?: boolean;
	resultTypes?: ResultType[];
	_selectedType?: number;
}

export const MissionPurpose: React.FC<MissionPurposeProps> = props => {
	const [localPurposes, setLocalPurposes] = useState<Purpose[]>([]);

	useEffect(() => {
		const bookingData = Store.bookingData

		if (
			bookingData &&
			Array.isArray(bookingData.purposes) &&
			bookingData.purposes.length > 0
		) {
			setLocalPurposes(bookingData.purposes)
			return
		}

		const selectedMission = props.content?.find(m => m.name === props.value)
		const purposes = selectedMission?.purposes ?? []

		const preparedPurposes = purposes.map((purpose, purposeIndex) => ({
			...purpose,
			_selected:
				typeof purpose._selected === 'boolean'
					? purpose._selected
					: purposeIndex === 0,
			resultTypes: (purpose.resultTypes ?? []).map(rt => ({
				...rt,
				_selected: !!rt._selected,
			})),
		}))

		setLocalPurposes(preparedPurposes)
	}, [props.value, props.content])

	const syncToParent = (updatedPurposes: Purpose[]) => {
		const missionObj = props.content?.find(m => m.name === props.value);

		if (missionObj) {
			missionObj.purposes = updatedPurposes;
		}

		const selectedResultTypeIds = Array.from(
			new Set(
				updatedPurposes
					.filter(purpose => purpose._selected)
					.flatMap(purpose =>
						(purpose.resultTypes ?? [])
							.filter(rt => rt._selected)
							.map(rt => rt.id)
					)
					.filter(Boolean)
			)
		);

		Store.setBookingData({
			...Store.bookingData,
			purposes: updatedPurposes,
			resultTypes: selectedResultTypeIds,
		});

		props.onChange?.();
	};

	const handleSelectPurpose = (index: number) => {
		const updated = localPurposes.map((purpose, purposeIndex) =>
			purposeIndex === index
				? {
					...purpose,
					_selected: !purpose._selected,
				}
				: purpose
		);

		setLocalPurposes(updated);
		syncToParent(updated);
	};

	if (!localPurposes.length) return null;

	return (
		<div className="flex flex-col gap-8 transition-all duration-300 ease-in-out" data-pw="mission-purpose-list">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-8 transition-all duration-300 ease-in-out">
				{localPurposes.map((p, i) => {
					const isSelected = !!p._selected;

					return (
						<div
							key={p.name || i}
							className="flex flex-col gap-5 transition-all duration-300 ease-in-out"
							data-pw={`purpose-item-${p.name || i}`}
							data-pw-selected={isSelected ? 'true' : 'false'}
						>
							<button
								type="button"
								onClick={() => handleSelectPurpose(i)}
								className={`relative overflow-hidden rounded-[18px] border-2 transition ${isSelected ? 'border-[#0063A3]' : 'border-transparent'
									}`}
								data-pw={`button-purpose-${p.name || i}`}
								data-pw-selected={isSelected ? 'true' : 'false'}
							>
								{p.image ? (
									<img
										src={
											p.image.startsWith('http')
												? p.image
												: `${Store.config.api.url.replace(/\/$/, '')}/${p.image.replace(/^\//, '')}`
										}
										alt={p.title || p.name || ''}
										className="w-full h-[180px] object-cover rounded-[16px]"
									/>
								) : (
									<div className="w-full h-[180px] rounded-[16px] bg-[#EEF4FA]" />
								)}

								{isSelected && (
									<div className="absolute top-3 right-3 w-8 h-8 bg-[#0063A3] rounded-full flex items-center justify-center">
										<svg
											width="18"
											height="18"
											viewBox="0 0 18 18"
											fill="none"
										>
											<path
												d="M4 9L7 12L14 5"
												stroke="white"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
								)}
							</button>

							<div>
								<h3 className="font-semibold text-[18px] text-[#2B2B2B]">
									{p.title || p.name || ''}
								</h3>

								<p className="text-[#666] text-[15px]">
									{p.description || ''}
								</p>
							</div>

							{isSelected && (p.resultTypes ?? []).length > 0 && (
								<div className="mt-3">
									<p className="font-semibold mb-3">
										Wofür benötigen Sie die Daten?
									</p>

									<RedesignDropdown
										key={`${props.value}-${p.name}`}
										content={p.resultTypes ?? []}
										multiple
										onChange={updatedResultTypes => {
											const updatedPurposes = localPurposes.map(
												(purpose, purposeIndex) =>
													purposeIndex === i
														? {
															...purpose,
															resultTypes: updatedResultTypes,
														}
														: purpose
											);

											setLocalPurposes(updatedPurposes);
											syncToParent(updatedPurposes);
										}}
										placeholder="Verwendungszweck wählen"
										data-pw={`dropdown-purpose-${p.name}`}
									/>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

//=============================================================================
// MissionType

interface Mission {
	name: string;
	title?: string;
	description?: string;
	icon?: string;
	purposes?: Array<{
		name: string;
		title?: string;
		description?: string;
		image?: string;
		_selected?: boolean;
		resultTypes?: Array<{
			id?: number;
			_selected?: boolean;
			name?: string;
			description?: string;
		}>;
		_selectedType?: number;
	}>;
}

interface BookingData {
	mission: string;
}

interface MissionTypeProps {
	missions: Mission[];
	bookingData: BookingData;
	onChange?: (missionName: string) => void;
}

export const MissionType: React.FC<MissionTypeProps> = props => {
	const [selected, setSelected] = useState<string>(props.bookingData.mission);

	   useEffect(() => {
		   let mission = props.bookingData.mission;
		   if (!mission) {
			   // fallback to first available mission if undefined or empty
			   mission = props.missions && props.missions.length > 0 ? props.missions[1].name : '';
		   }
		   setSelected(mission);
		   console.log('misija ', mission)
	   }, [props.bookingData.mission, props.missions]);

	const onSelect = (missionName: string) => {
		setSelected(missionName);

		Store.setBookingData({
			...Store.bookingData,
			mission: missionName,
			purposes: [],
			resultTypes: [],
		});

		props.onChange?.(missionName);
	};

	return (
		<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
			{props.missions
				?.filter(m => m.purposes?.length)
				.map(mission => {
					const isSelected = selected === mission.name;

					return (
						<button
							key={mission.name}
							type="button"
							data-pw={`button-mission-type-${mission.name}`}
							onClick={() => onSelect(mission.name)}
							className={[
								'relative w-full rounded-[20px] min-h-[136px] px-6 py-4 text-left',
								'bg-[#EEF4FA] border transition-all duration-200',
								'flex items-start gap-6',
								isSelected
									? 'border-[#0063A3] shadow-[0_0_0_1px_#0063A3]'
									: 'border-[#DCE9F5] hover:border-[#B7CCDD]',
							].join(' ')}
						>
							{isSelected && (
								<div className="absolute top-5 right-5 flex h-8 w-8 items-center justify-center rounded-full bg-[#0063A3]">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="28"
										height="28"
										viewBox="0 0 24 24"
										fill="none"
									>
										<path
											d="M5 12.5L10 17L19 7"
											stroke="white"
											strokeWidth="2.5"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</div>
							)}

							<div className="shrink-0 mt-1">
								<AnalyseIcon />
							</div>

							<div className="pr-12">
								<h3 className="text-xl leading-[32px] font-semibold text-[#2C2C2C] mb-4">
									{mission.title || mission.name || ''}
								</h3>
								<p className="text-base text-[#666666]">
									{mission.description || ''}
								</p>
							</div>
						</button>
					);
				})}
		</div>
	);
};
//============================================================================= Input
interface InputProps {
	id?: string;
	label?: string;
	value?: string;
	placeholder?: string;
	password?: boolean;
	type?: string;
	required?: boolean;
	invalid?: boolean;
	maxLength?: number;
	autoFocus?: boolean;
	search?: boolean;
	className?: string;
	onChange?: (value: string, id?: string) => void;
	onSubmit?: (value: string) => void;
	onSearch?: (value: string) => void;
	onEnter?: (value: string) => void;
	children?: React.ReactNode;
	step?: number;
}

export const Input: React.FC<InputProps> = props => {
	const [value, setValue] = useState<string>(props.value ?? '');
	const inputId = props.id ?? (props.label ? `input-${props.label.replace(/[^a-z0-9_-]+/gi, '-').toLowerCase()}` : undefined);

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value);
		props.onChange?.(e.target.value, props.id);
	};

	useEffect(() => {
		setValue(props.value ?? '');
	}, [props.value]);

	return (
		<div className={"flex flex-col gap-2 w-full relative" + (props.className ? ' ' + props.className : '')}>
			{props.label && <label htmlFor={inputId}>{props.label}</label>}

			<input
				id={inputId}
				data-pw={(props as any)['data-pw'] ?? props.id}
				className={'styled-input' + (props.invalid ? ' invalid' : '')}
				required={props.required}
				placeholder={props.placeholder}
				type={props.password ? 'password' : props.type ?? 'text'}
				value={value}
				min={0}
				step={props.step ?? 1}
				maxLength={props.maxLength}
				onChange={onChange}
				autoFocus={props.autoFocus}
				onKeyDown={e => {
					if (e.key === 'Enter') {
						props.onSubmit?.(value);
						props.onSearch?.(value);
						props.onEnter?.(value);
					}
				}}
			/>

			{props.invalid && (
				<div className="inline-flex gap-2 p-1 items-center">
					<IconInfoError />
					<h6>{'Eingabe erforderlich'}</h6>
				</div>
			)}

			{props.search && (
				<button
					className="styled-input-search-icon"
					data-pw="button-input-search-icon"
					onClick={e => props.onSearch?.(value)}
				>
					<IconSearch />
				</button>
			)}

			{props.children}
		</div>
	);
};


//============================================================================= InputButton
interface InputButtonProps {
	id?: string;
	value?: string;
	placeholder?: string;
	password?: boolean;
	onChange?: (value: string, id?: string) => void;
	onClick?: (value: string) => void;
}

export const InputButton: React.FC<InputButtonProps> = props => {
	const [value, setValue] = useState<string>(props.value ?? '');

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value);
		props.onChange?.(e.target.value, props.id);
	};

	return (
		<div className="flex w-full gap-3">
			<input
				className="styled-input"
				placeholder={props.placeholder}
				type={props.password ? 'password' : 'text'}
				value={value}
				onChange={onChange}
				onKeyDown={e => {
					if (e.key === 'Enter') props.onClick?.(value);
				}}
			/>
			<button
				className="styled-input-button"
				data-pw="button-input-button"
				onClick={e => props.onClick?.(value)}>
				Suchen
			</button>
		</div>
	);
};


//============================================================================= GeoSearchInput
interface GeoSearchInputProps {
	locations?: Array<{ display_name: string }>;
	onResult?: (result: Array<{ display_name: string }>) => void;
	onSelect?: (select: { display_name: string }) => void;
}

export const GeoSearchInput: React.FC<GeoSearchInputProps> = props => {
	const [locations, setLocations] = useState<Array<{ display_name: string }>>([]);

	useEffect(() => {
		setLocations(props.locations ?? []);
	}, [props.locations]);

	// ------------------------------------------------------------------------ onGeoSearch
	const onGeoSearch = (input: string) => {
		console.log('---geoSearch:', input, '...');
		// props.onGeosearch?.(input)

		// https://api.geoapify.com/v1/geocode/autocomplete?text=Mosco&apiKey=83623e1c7a344d6a8a84e2e67acc0105
		// let url = 'https://api.geoapify.com/v1/geocode/autocomplete?text=' + input + '&limit=10&apiKey=83623e1c7a344d6a8a84e2e67acc0105'
		let url = 'https://nominatim.openstreetmap.org/search?q=' + input + '&format=json';

		fetch(url)
			.then(response => response.json())
			.then(result => {
				console.log('result:', result);
				setLocations(result);
				props.onResult?.(result);

				// considering that the buttons are only available after the dropdown is shown, we can add the data-pw attribute to them
				let buttons = document.querySelectorAll('.leaflet-input-box ul button');
				buttons.forEach((button, index) => {
					button.setAttribute('data-pw', 'input-geo-search-button-' + index);
				});
			})
			.catch(err => {
				console.log('error:', err);
				setLocations([]);
				props.onResult?.([]);
			});
	};

	// ------------------------------------------------------------------------ onSelect
	const onSelect = (select: { display_name: string }) => {
		console.log('---select:', select);
		props.onSelect?.(select);
	};

	return (
		<InputSelect
			id="input-geo-search"
			data-pw="input-geo-search"
			placeholder="Ziel suchen"
			noFilter
			autoFocus
			options={locations}
			labelField="display_name"
			onChange={onGeoSearch}
			onChangeDelay={500}
			onEnter={onGeoSearch}
			onSelect={onSelect}
		/>
	);
};


//============================================================================= InputSelect
/* props:
	value
	options
	onChange
	noFilter	// to show all options otherwise only the matched ones
	onChangeDelay	// for e delayed onChange callback
	onKeyDown
	placeholder
	autoFocus

TODOS:
	disabled
	invalid
	required
*/
// Use a generic type T for options, defaulting to Record<string, any> for flexibility
interface InputSelectProps<T = Record<string, any>> {
	value?: string;
	options?: Array<T>; // Use generic T
	labelField?: keyof T | string; // Use keyof T for better type safety, fallback to string
	noFilter?: boolean;
	onChange?: (value: string) => void;
	onChangeDelay?: number;
	onEnter?: (value: string) => void;
	onSelect?: (option: T) => void; // Use generic T
	autoFocus?: boolean;
	placeholder?: string;
	id?: string;
}

// Use generic function component syntax and constrain T
export const InputSelect = <T extends Record<string, any>>(props: InputSelectProps<T>) => {
	const [query, setQuery] = useState<string>(props.value ?? '');
	const [activeIndex, setActiveIndex] = useState<number>(-1);
	const [showOptions, setShowOptions] = useState<boolean>(false);
	const [options, setOptions] = useState<Array<T>>([]); // Use generic T
	const timeout = useRef<NodeJS.Timeout | null>(null);
	// Assert type for label access, provide default 'label'
	const label = (props.labelField as keyof T) ?? ('label' as keyof T);

	useEffect(() => {
		setOptions(props.options ?? []);
	}, [props.options]);

	useEffect(() => {
		setQuery(props.value ?? '');
	}, [props.value]);

	const filteredOptions = props.noFilter
		? options
		: options.filter(option =>
			// Ensure the label field value is treated as a string for comparison
			String(option[label]).toLowerCase().includes(query.toLowerCase())
		);

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		// Check if timeout.current exists before clearing
		if (timeout.current) {
			clearTimeout(timeout.current);
		}

		if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
			setActiveIndex(prevIndex => (prevIndex + 1) % filteredOptions.length);
		} else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
			setActiveIndex(prevIndex => (prevIndex - 1 + filteredOptions.length) % filteredOptions.length);
		} else if (e.key === 'Enter') {
			if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
				handleOptionClick(filteredOptions[activeIndex]);
			} else {
				props.onEnter?.(query);
			}
		}
	};

	// Use generic type T for the option parameter
	const handleOptionClick = (option: T) => {
		// Check if timeout.current exists before clearing
		if (timeout.current) {
			clearTimeout(timeout.current);
		}
		// Ensure the label field value is treated as a string for setQuery
		setQuery(String(option[label]));
		setShowOptions(false);
		props.onSelect?.(option);
	};

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		// Check if timeout.current exists before clearing
		if (timeout.current) {
			clearTimeout(timeout.current);
		}
		setQuery(e.target.value);
		setShowOptions(true);
		setActiveIndex(-1);

		if (!props.onChangeDelay) {
			props.onChange?.(e.target.value);
		} else {
			timeout.current = setTimeout(() => props.onChange?.(e.target.value), props.onChangeDelay);
		}
	};

	return (
		<div className="w-full relative p-0 m-0">
			<input
				id={props.id}
				className="styled-input select"
				data-pw={(props as any)['data-pw'] ?? props.id}
				type="text"
				value={query}
				autoFocus={props.autoFocus}
				onChange={onChange}
				onKeyDown={handleKeyDown}
				onFocus={() => setShowOptions(true)}
				onBlur={() => setTimeout(() => setShowOptions(false), 100)}
				placeholder={props.placeholder}
			/>
			{showOptions && query && filteredOptions.length > 0 && (
				<ul className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg">
					{filteredOptions.map((option, index) => (
						<button
							data-pw="button-input-dropdown-item"
							key={index}
							className={`px-4 py-2 cursor-pointer ${index === activeIndex ? 'bg-blue-500 text-white' : ''}`}
							onMouseDown={(e) => handleOptionClick(option)}
							onMouseEnter={() => setActiveIndex(index)}
						>
							{option[label]}
						</button>
					))}
				</ul>
			)}
			{filteredOptions.length > 0 && (
				<button
					data-pw="button-input-dropdown-clear"
					onClick={() => {
						setQuery('');
						props.onChange?.('');
					}}
					className="absolute right-0 h-full px-3 text-gray-400 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
				>
					<svg
						className="h-5 w-5"
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						strokeWidth="1.5"
						stroke="currentColor"
					>
						<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path>
					</svg>
				</button>
			)}
		</div>
	);
}


//============================================================================= PurposeInfo
interface PurposeInfoProps {
	purposes: Array<{
		name: string;
		resultTypes: Array<{ name: string }>;
	}>;
}

export const PurposeInfo: React.FC<PurposeInfoProps> = props => {
	return (
		<div>
			{props.purposes.map((e, i) => (
				<div key={i}>
					{e.name + ':'}
					{e.resultTypes.map((rt, j) => (
						<span key={j}>{rt.name}</span>
					))}
				</div>
			))}
		</div>
	);
};


//============================================================================= Panel
interface PanelProps {
	children?: React.ReactNode;
}

export const Panel: React.FC<PanelProps> = props => {
	return (
		<div className='styled-panel'>
			{props.children}
		</div>
	);
};



//============================================================================= CardGrid
interface CardGridProps {
	children?: React.ReactNode;
}

export const CardGrid: React.FC<CardGridProps> = props => {
	return (
		<div className='card-grid'>
			{props.children}
		</div>
	);
};



//============================================================================= CardGrid
interface CardItemProps {
	onClick?: () => void;
	children?: React.ReactNode;
}

export const CardItem: React.FC<CardItemProps> = props => {
	return (
		<button className='card-item' onClick={props.onClick} data-pw="button-card-item">
			{props.children}
		</button>
	);
};


//============================================================================= OrderResults
interface OrderResult {
	name: string;
	purpose: string;
	downloadUrl: string;
	resultType: string;
	fileName: string;
}

interface OrderResultsProps {
	orderResults: OrderResult[];
	orderState: string;
	mapSetup: {
		setNDVI?: (value: boolean) => void;
	};
}

export const OrderResults: React.FC<OrderResultsProps> = ({ orderResults, orderState, mapSetup }) => {
	const [flightImages, setFlightImages] = useState<string[]>([])
	const [flightLoading, setFlightLoading] = useState(false)
	const [flightDone, setFlightDone] = useState(false)
	const [showGallery, setShowGallery] = useState(false)
	const [lightboxImage, setLightboxImage] = useState<string | null>(null)

	const djiHeaders = () => {
		const token = localStorage.getItem('dji_user_token')
		return {
			'X-Request-Id': '',
			'X-Language': 'zh',
			'X-Project-Uuid': '632e4e14-da95-40e8-9d6e-3060722d7f99',
			'Content-Type': 'application/json',
			'X-USER-TOKEN': token || '',
		}
	}

	const fetchFlightImages = async () => {
		const token = localStorage.getItem('dji_user_token')
		const taskUuid = localStorage.getItem('dji_task_uuid')
		if (!token || !taskUuid) {
			Store.setPopupMessage('info', 'Fehler', 'Bitte _user_token und dji_task_uuid im localStorage setzen.')
			return
		}
		setFlightLoading(true)
		try {
			// Step 1: Check flight task status
			const statusRes = await fetch(`https://es-flight-api-eu.djigate.com/openapi/v0.1/flight-task/${taskUuid}`, {
				headers: djiHeaders(),
			})
			const statusData = await statusRes.json()
			console.log('DJI flight task status:', statusData)

			if (statusData?.data?.status !== 'success') {
				Store.setPopupMessage('info', 'Status', `Flugauftrag Status: ${statusData?.data?.status || 'unbekannt'}`)
				return
			}

			// Step 2: Fetch media
			const mediaRes = await fetch(`https://es-flight-api-eu.djigate.com/openapi/v0.1/flight-task/${taskUuid}/media`, {
				headers: djiHeaders(),
			})
			const mediaData = await mediaRes.json()
			console.log('DJI flight task media:', mediaData)

			const list = mediaData?.data?.list || []
			const previewUrls = list
				.filter((item: any) => item.file_type === 'image')
				.slice(0, 3)
				.map((item: any) => item.preview_url)

			setFlightImages(previewUrls)
			setFlightDone(true)
		} catch (err) {
			console.error('DJI flight task fetch error:', err)
			Store.setPopupMessage('info', 'Fehler', 'Fehler beim Laden der Flugdaten.')
		} finally {
			setFlightLoading(false)
		}
	}

	if (!orderResults) return null

	return (
		<div className='order-results'>
			<h3>Auswertung der Befliegung</h3>
			<div className='w-full flex flex-col gap-3'>
				{orderResults.map((e, i) =>
					<DownloadButton key={i} data-pw="button-order-results-download" label={e.purpose + " herunterladen"} url={e.downloadUrl} self />
				)}
				{orderState.endsWith('COMPLETED') && (
					<>
						<DownloadButton data-pw="button-order-results-ndvi" label={'Auswertung anzeigen'} onClick={() => {
							console.log('load ndvi demo ...')
							mapSetup.setNDVI?.(true)
						}} />
						<DownloadButton label={'Auswertung herunterladen'} onClick={() => {
							window.open(Store.config.view3d, '_blank')
						}} />
					</>
				)}

				<DownloadButton
					label={flightLoading ? 'Laden...' : 'Bilder downloaden (DJI)'}
					onClick={fetchFlightImages}
				/>

				{flightImages.length > 0 && (
					<DownloadButton
						label='Bilder anzeigen'
						onClick={() => setShowGallery(true)}
					/>
				)}

				{showGallery && (
					<div
						className='fixed inset-0 z-50 bg-black/80 flex flex-col items-center justify-center p-4'
						onClick={() => { setShowGallery(false); setLightboxImage(null) }}
					>
						{lightboxImage ? (
							<img
								src={lightboxImage}
								alt='Flugbild groß'
								className='max-h-[90vh] max-w-[90vw] rounded shadow-2xl'
								onClick={e => e.stopPropagation()}
							/>
						) : (
							<div
								className='bg-white rounded-xl p-4 max-h-[90vh] overflow-y-auto w-full max-w-2xl'
								onClick={e => e.stopPropagation()}
							>
								<div className='flex justify-between items-center mb-3'>
									<h3 className='text-lg font-semibold'>Flugbilder</h3>
									<button className='text-gray-500 hover:text-gray-800 text-xl leading-none' onClick={() => setShowGallery(false)}>✕</button>
								</div>
								<div className='grid grid-cols-2 gap-2'>
									{flightImages.map((url, i) => (
										<img
											key={i}
											src={url}
											alt={`Flugbild ${i + 1}`}
											className='w-full rounded border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity'
											onClick={() => setLightboxImage(url)}
										/>
									))}
								</div>
							</div>
						)}
					</div>
				)}

				{flightDone && (
					<div className='text-green-600 font-semibold text-center mt-2'>
						Alle heruntergeladen
					</div>
				)}
			</div>
		</div>
	)
}


//============================================================================= OrderList
interface OrderListProps {
	title?: string;
	orders: Array<{ name: string; orderId: string; orderState: string; dateFrom: string; dateTo: string; mission: string; price: number }>;
	interactive?: boolean;
	noPrice?: boolean; // Optional prop to hide price
	dashboard?: boolean; // Optional prop to adjust styling for dashboard context
}

export const OrderList: React.FC<OrderListProps> = ({ title, orders, interactive, noPrice, dashboard }) => {
	const hoveredOrder = Store.use('hoveredOrder')

	return (
		<div className='order-container'>
			{!orders?.length ?
				<div>keine Daten vorhanden</div>
				:
				<div className='order-list'>
					{orders
						.map((e, i) => <OrderItem
							dashboard={dashboard}
							noPrice={noPrice}
							interactive={interactive}
							hovered={hoveredOrder === e.orderId}
							key={i}
							data={{ ...e, orderId: e.orderId ?? 'default-order-id' }}
							onClick={() => Store.selectOrder(e)} // Pass e.name to Store.selectOrder
						/>)}
				</div>
			}
		</div>
	)
}

export const ServiceCardGrid = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const [columnCount, setColumnCount] = useState(3);

	useEffect(() => {
		const updateColumns = () => {
			if (window.innerWidth >= 1024) {
				setColumnCount(3);
			} else if (window.innerWidth >= 640) {
				setColumnCount(2);
			} else {
				setColumnCount(1);
			}
		};

		updateColumns();
		window.addEventListener("resize", updateColumns);

		return () => window.removeEventListener("resize", updateColumns);
	}, []);

	const childArray = React.Children.toArray(children);

	const columns = useMemo(() => {
		const result: React.ReactNode[][] = Array.from(
			{ length: columnCount },
			() => []
		);

		childArray.forEach((child, index) => {
			result[index % columnCount].push(child);
		});

		return result;
	}, [childArray, columnCount]);

	return (
		<div className="grid gap-6 items-start grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
			{columns.map((column, colIndex) => (
				<div key={colIndex} className="flex flex-col gap-6">
					{column.map((child, index) => (
						<React.Fragment key={index}>{child}</React.Fragment>
					))}
				</div>
			))}
		</div>
	);
};
interface ServiceCardProps {
	icon?: string;
	title: string;
	description: string;
	features?: string[];
	price?: string;
	active?: boolean;
	className?: string;
	children?: React.ReactNode;
	id: string;
	isLast?: boolean;
	onSelect?: (id: string) => void;
}

export const ServiceCard = ({
	id,
	icon,
	title,
	description,
	features = [],
	price,
	className = "",
	children = null,
	onSelect,
	isLast = false,
}: ServiceCardProps) => {
	const [expanded, setExpanded] = useState(false);

	return (
		<div
			className={`flex flex-col justify-between pb-[3px]
	rounded-2xl transition w-full
	bg-[#EEF4FB] min-h-[300px]
	shadow-[0_4px_16px_0_rgba(0,36,100,0.10)]
	border border-[#DCE9F5]
	${isLast ? "bg-[#F2F2F2] border-[#E5E5E5]" : ""}
	${className}`}
		>
			<div className="p-6 pb-0 min-h-[231px]">
				   {icon && (
					   <img
						   className="mb-4 w-8 h-8"
						   src={
							   isLast
								   ? icon
								   : (icon && (icon.startsWith('http')
									   ? icon
									   : `${Store.config.api.url.replace(/\/$/, '')}/${icon.replace(/^\//, '')}`))
						   }
						   alt={title}
					   />
				   )}

				<p className="font-semibold text-xl leading-snug mb-2 text-333">
					{title}
				</p>

				<p className={`text-[#666666] text-base mb-4 ${!expanded ? "line-clamp-4" : ""}`}>
					{description}
				</p>

				{expanded && (
					<>
						{features.length > 0 && (
							<div className="text-left text-sm mb-4">
								<p className="font-semibold text-333 mb-2">Auswertung</p>
								<ul className="space-y-1">
									{features.map((f, i) => (
										<li key={i} className="flex items-center gap-1.5 text-base">
											<span className="text-blue-500">
												<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
													<path d="M7.5 13.75C10.9375 13.75 13.75 10.9375 13.75 7.5C13.75 4.0625 10.9375 1.25 7.5 1.25C4.0625 1.25 1.25 4.0625 1.25 7.5C1.25 10.9375 4.0625 13.75 7.5 13.75Z" stroke="#0063A3" strokeLinecap="round" strokeLinejoin="round" />
													<path d="M4.84375 7.5002L6.6125 9.26895L10.1562 5.73145" stroke="#0063A3" strokeLinecap="round" strokeLinejoin="round" />
												</svg>
											</span>
											{f}
										</li>
									))}
								</ul>
							</div>
						)}

						{price && (
							<div>
								<p className="justify-self-center text-lg font-semibold mb-4 text-333">
									ab {price}
								</p>
								<Button
									data-pw={`button-service-card-${id}-select`}
									className="mt-auto"
									primary
									onClick={() => onSelect?.(id)}
								>
									Auswählen
								</Button>
							</div>
						)}

						{children}
					</>
				)}
			</div>

			<div>
				<div className="mx-6 border-t border-gray-200/60" />

				<button
					data-pw={`button-service-card-${id}-toggle-details`}
					onClick={() => setExpanded(!expanded)}
					className="w-full px-6 py-3 text-sm flex items-center justify-center gap-1 hover:underline transition"
				>
					{expanded ? "Details ausblenden" : "Details einblenden"}
					{expanded ? (
						<KeyboardArrowUpIcon fontSize="small" />
					) : (
						<KeyboardArrowDownIcon fontSize="small" />
					)}
				</button>
			</div>
		</div>
	);
};
